import { useMemo, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { useAuth } from "@/shared/hooks";
import { PERMISSIONS } from "@/shared/consts/permissions";
import type { RangeValue } from "@/shared/ui";
import type { Locale } from "@/shared/utils";
import { useGetProjectTableQuery } from "../api";
import { formatISO } from "../utils";
import { generateProjectTableXlsx } from "../utils/projectTableExport";
import type { ProjectTableEntry, ProjectTableExpenseItem } from "../types";
import type { ProjectVM, GroupVM, RecordVM } from "../pages/ProjectTablePage/types";

function buildParams(range: RangeValue): { start_date?: string; end_date?: string } | undefined {
  const params: { start_date?: string; end_date?: string } = {};
  if (range.start) params.start_date = formatISO(range.start);
  if (range.end) params.end_date = formatISO(range.end);
  return params.start_date || params.end_date ? params : undefined;
}

function toCents(value: string | null | undefined): number {
  return Math.round((parseFloat(value || "0") || 0) * 100);
}

function fromCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

function expenseCompany(item: ProjectTableExpenseItem): string {
  if (item.source_type === "payroll_entry") {
    const name = item.employee.full_name || "—";
    return item.employee.position ? `${name} (${item.employee.position})` : name;
  }
  if (item.source_type === "gph_payment") {
    return item.contractor.name || "—";
  }
  return item.vendor.name || "—";
}

function buildIncomeGroups(entry: ProjectTableEntry, t: TFunction): GroupVM[] {
  const groups = new Map<string, GroupVM>();
  for (const row of entry.income) {
    if (!row.amount || toCents(row.amount) === 0) continue;
    let group = groups.get(row.income_type);
    if (!group) {
      group = {
        key: `${entry.project_id}:income:${row.income_type}`,
        kind: "income",
        label: t(`incomeType.${row.income_type}`, { defaultValue: row.income_type }),
        total: "0.00",
        records: [],
      };
      groups.set(row.income_type, group);
    }
    group.records.push({
      key: `inc-${row.id}`,
      date: row.document_date,
      company: row.source_name || "—",
      category: "",
      amount: row.amount,
    });
  }
  for (const group of groups.values()) {
    group.total = fromCents(group.records.reduce((sum, r) => sum + toCents(r.amount), 0));
  }
  return [...groups.values()];
}

function buildExpenseGroups(entry: ProjectTableEntry, t: TFunction): GroupVM[] {
  const groups: GroupVM[] = [];
  for (const section of entry.sections) {
    for (const subcategory of section.subcategories) {
      if (!subcategory.items?.length) continue;
      const records: RecordVM[] = subcategory.items.map(function mapItem(item) {
        return {
          key: `je-${item.journal_entry_id}`,
          date: item.date,
          company: expenseCompany(item),
          category: t(`entryType.${item.entry_type}`, { defaultValue: item.entry_type }),
          amount: item.amount,
        };
      });
      groups.push({
        key: `${entry.project_id}:expense:${subcategory.id}`,
        kind: "expense",
        label: t(`subcategory.${subcategory.id}`, { defaultValue: subcategory.id }),
        total: subcategory.total,
        records,
      });
    }
  }
  return groups;
}

function buildProjectVM(entry: ProjectTableEntry, t: TFunction): ProjectVM {
  return {
    id: entry.project_id,
    label: entry.project_label,
    isInternal: entry.is_internal === true,
    initialBalance: entry.initial_balance,
    totalIncome: fromCents(toCents(entry.initial_balance) + toCents(entry.total_revenue)),
    totalExpense: entry.total_expenses,
    balance: entry.total_balance,
    incomeGroups: buildIncomeGroups(entry, t),
    expenseGroups: buildExpenseGroups(entry, t),
  };
}

export function useProjectTablePage() {
  const { t, i18n } = useTranslation("ProjectTablePage");
  const locale = (i18n.language || "ru") as Locale;
  const { hasPermission } = useAuth();
  const canView = hasPermission(PERMISSIONS.ACCOUNTING.VIEW_PROJECT_TABLE);

  const [dateRange, setDateRange] = useState<RangeValue>({ start: null, end: null });
  const [isExporting, setIsExporting] = useState(false);

  const handleDateRangeChange = useCallback(function handleDateRangeChange(value: RangeValue | null) {
    setDateRange(value ?? { start: null, end: null });
  }, []);

  const queryParams = useMemo(function computeParams() {
    return buildParams(dateRange);
  }, [dateRange]);

  const { data, isLoading, isFetching, isError } = useGetProjectTableQuery(queryParams, { skip: !canView });

  const projects = useMemo<ProjectVM[]>(function buildProjects() {
    if (!data) return [];
    return data.projects.map(function mapEntry(entry) {
      return buildProjectVM(entry, t);
    });
  }, [data, t]);

  const handleDownload = useCallback(
    async function handleDownload() {
      if (isExporting || projects.length === 0) return;
      try {
        setIsExporting(true);
        await generateProjectTableXlsx(projects, locale);
      } finally {
        setIsExporting(false);
      }
    },
    [isExporting, projects, locale],
  );

  const isEmpty = !isLoading && projects.length === 0;

  return {
    projects,
    isLoading,
    isFetching,
    isError,
    isEmpty,
    dateRange,
    onDateRangeChange: handleDateRangeChange,
    handleDownload,
    isExporting,
    locale,
  };
}
